import {
    CTA,
    Select,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import {Map} from '@volkswagen-onehub/icons-core'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Province} from '../../types/province'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import isEmpty from 'lodash.isempty'
import {DealerData} from '../../types/dealer'
import {capitalizeWords} from '../../shared/utilities'
import {Loading} from '../components/loading'

const ResultViewContainer = styled.div`
    margin-top: ${(props) => props.theme.size.dynamic0200};
`
const DealerResult = styled.div`
    padding-bottom: ${(props) => props.theme.size.static250};
    margin-bottom: ${(props) => props.theme.size.static400};
    border-bottom: ${(props) => props.theme.size.border.light} solid
        ${(props) => props.theme.colors.border.default};
    display: grid;
    gap: ${(props) => props.theme.size.static150};
`
const DealerAlertTag = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0 0 ${(props) => props.theme.size.dynamic0050};
    background-color: ${(props) => props.theme.colors.signal.promo};
    padding: ${(props) => props.theme.size.dynamic0050};
    border: ${(props) => props.theme.size.border.heavy} solid
        ${(props) => props.theme.colors.border.card};
    border-radius: ${(props) => props.theme.size.borderRadius.cardS};
    legend {
        font-weight: normal;
    }
`
const ResultViewContainerTitle = styled(Text)`
    margin-bottom: ${(props) => props.theme.size.static400};
`
const StyledSelectMessage = styled.div`
    position: absolute;
    left: -9999px;
`
// tslint:disable-next-line:cyclomatic-complexity
export function DealerView(): React.ReactElement {
    const {t} = useTranslation('fa-coupon')

    const allProvinces = useSelector(
        (state: RootState) => state.provinces.allProvinces
    )
    const allDealers = useSelector(
        (state: RootState) => state.dealers.allDealers
    )
    const brand = useSelector((state: RootState) => state.general.brand)
    const myDealerCode = useSelector(
        (state: RootState) => state.dealers.myDealerCode
    )
    const myDealerBranches = useSelector(
        (state: RootState) => state.dealers.myDealerBranches
    )

    const dealerBranchCodes =
        Array.isArray(myDealerBranches) && myDealerBranches.length > 0
            ? myDealerBranches.map((branch: DealerData) => branch.bearer)
            : []

    const isDealerizedUser =
        Boolean(myDealerCode) || dealerBranchCodes.length > 0

    const myDealerCodesToCheck = [
        ...(myDealerCode ? [myDealerCode] : []),
        ...dealerBranchCodes,
    ]

    const myDealerIsInPromo =
        !isEmpty(allDealers) &&
        myDealerCodesToCheck.length > 0 &&
        allDealers.some((dealer: DealerData) =>
            myDealerCodesToCheck.includes(dealer.companyCode)
        )

    const showDealerNotInPromoBanner =
        !isEmpty(allDealers) && isDealerizedUser && !myDealerIsInPromo

    const primaryDealerFromBranches =
        Array.isArray(myDealerBranches) && myDealerBranches.length > 0
            ? (myDealerBranches[0] as DealerData)
            : undefined

    const primaryDealerFromAllDealers =
        !primaryDealerFromBranches && myDealerCode
            ? allDealers.find(
                  (dealer: DealerData) => dealer.companyCode === myDealerCode
              )
            : undefined

    const primaryDealer =
        primaryDealerFromBranches || primaryDealerFromAllDealers

    const myDealerName = primaryDealer
        ? capitalizeWords(
              primaryDealer.cleanedCompany ||
                  primaryDealer.company ||
                  primaryDealer.commercialName
          )
        : ''

    const [filteredDealers, setFilteredDealers] = useState<DealerData[]>([])
    const [searchActive, setSearchActive] = useState(false)
    const [provinceStatusMessage, setProvinceStatusMessage] = useState<string>(
        t('form.province.noneSelected')
    )
    const filterDealersByProvince = (province: string) => {
        setFilteredDealers(
            allDealers.filter(
                (dealer: DealerData) => dealer.provinceCode === province
            )
        )
        setProvinceStatusMessage(
            province
                ? t('form.province.selected', {
                      province,
                  })
                : t('form.province.noneSelected')
        )
    }

    return (
        <>
            {!isEmpty(allProvinces) && !isEmpty(allDealers) ? (
                <div>
                    {showDealerNotInPromoBanner && (
                        <DealerAlertTag aria-live="polite" role="status">
                            <Text
                                tag={TextTag.p}
                                bold
                                appearance={TokenTextAppearance.headline100}
                                color={TokenTextColor.inverted}
                            >
                                {myDealerName
                                    ? t(
                                          'dealerView.myDealerNotInPromo.titleWithName',
                                          {
                                              dealerName: myDealerName,
                                          }
                                      )
                                    : t('dealerView.myDealerNotInPromo.title')}
                            </Text>

                            <Text
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.copy100}
                                color={TokenTextColor.inverted}
                            >
                                {t('dealerView.myDealerNotInPromo.description')}
                            </Text>
                        </DealerAlertTag>
                    )}

                    <Select
                        defaultValue=""
                        onChange={(val) => {
                            setSearchActive(true)
                            filterDealersByProvince(val.currentTarget.value)
                        }}
                        label={t('form.province.label')}
                    >
                        <option value="">{t('form.province.default')}</option>
                        {allProvinces.map((option: Province) => (
                            <option value={option.code} key={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </Select>

                    <StyledSelectMessage role="status" aria-live="polite">
                        {provinceStatusMessage}
                    </StyledSelectMessage>

                    {searchActive ? (
                        <ResultViewContainer>
                            <ResultViewContainerTitle
                                tag={TextTag.p}
                                bold
                                appearance={TokenTextAppearance.headline200}
                            >
                                {isEmpty(filteredDealers) ? (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: t(
                                                `dealerView.titleNoResults.${brand}`
                                            ),
                                        }}
                                    />
                                ) : undefined}
                            </ResultViewContainerTitle>

                            {!isEmpty(filteredDealers) && searchActive ? (
                                <>
                                    {filteredDealers.map(
                                        (dealer: DealerData) => (
                                            <DealerResult key={dealer.id}>
                                                <Text
                                                    tag={TextTag.h3}
                                                    bold
                                                    appearance={
                                                        TokenTextAppearance.headline100
                                                    }
                                                >
                                                    {capitalizeWords(
                                                        dealer.cleanedCompany
                                                    )}
                                                </Text>
                                                <Text
                                                    tag={TextTag.p}
                                                    appearance={
                                                        TokenTextAppearance.headline100
                                                    }
                                                >
                                                    {capitalizeWords(
                                                        `${dealer.address} ${
                                                            dealer.streetNumber
                                                                ? dealer.streetNumber +
                                                                  ', '
                                                                : ''
                                                        } ${dealer.city}`
                                                    )}
                                                </Text>
                                                <CTA
                                                    tag="a"
                                                    target="_blank"
                                                    emphasis="tertiary"
                                                    href={`https://www.volkswagen.it/it/rete-volkswagen/informazioni.html/__app/${dealer.companyCode}.app`}
                                                    aria-label={t(
                                                        'dealerView.map'
                                                    )}
                                                    icon={<Map aria-hidden />}
                                                    iconPosition="end"
                                                >
                                                    {t('dealerView.map')}
                                                </CTA>
                                            </DealerResult>
                                        )
                                    )}
                                </>
                            ) : undefined}
                        </ResultViewContainer>
                    ) : undefined}
                </div>
            ) : (
                <Loading />
            )}
        </>
    )
}
